import React, { useState } from 'react';
import { Badge } from 'react-bootstrap';
import { FaMapMarkerAlt, FaExternalLinkAlt, FaDoorOpen, FaChevronDown } from 'react-icons/fa';
import { realEstateListingsApi } from '../../_services/api/realEstate/listings';

/**
 * Rich property card for the RE side panel.
 * Supports single-label tagging (like EVV visits) with colored badges.
 */
function PropertyCard({ item, selected, onClick, onOpenChannel, onLabelChange }) {
    const [labelOpen, setLabelOpen] = useState(false);
    const [labelLoading, setLabelLoading] = useState(false);

    const priceStr = item.price != null ? `$${Number(item.price).toLocaleString()}` : 'Price TBD';
    const line2 = [item.city, item.state, item.postalCode].filter(Boolean).join(', ');
    const isOZ = item.opportunityZone;

    const isTargeted = item.discoverySource === 'philadelphia_targeted';
    const labelColor = realEstateListingsApi.RE_LABELS[item.label];
    const labelDisplay = realEstateListingsApi.RE_LABEL_OPTIONS.find((o) => o.value === item.label)?.label;

    const handleLabelSelect = async (newLabel) => {
        setLabelOpen(false);
        if (newLabel === (item.label || '')) return;
        setLabelLoading(true);
        try {
            await realEstateListingsApi.setPropertyLabel(item.id, newLabel);
            if (onLabelChange) onLabelChange(item.id, newLabel);
        } catch (e) {
            console.error('Failed to set label', e);
        } finally {
            setLabelLoading(false);
        }
    };

    return (
        <div
            onClick={onClick}
            style={{
                cursor: 'pointer',
                padding: '12px 14px',
                borderBottom: '1px solid #eee',
                background: selected ? '#e8f4fd' : 'transparent',
                transition: 'background 0.15s',
                position: 'relative',
            }}
            onMouseEnter={(e) => {
                if (!selected) e.currentTarget.style.background = '#f7f9fc';
            }}
            onMouseLeave={(e) => {
                if (!selected) e.currentTarget.style.background = 'transparent';
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#222' }}>
                        <FaMapMarkerAlt className="mr-1" style={{ color: '#d64545', fontSize: 12 }} />
                        {item.address || '(no address)'}
                    </div>
                    <div style={{ color: '#777', fontSize: 12, marginTop: 2 }}>{line2 || '-'}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1a73e8' }}>{priceStr}</div>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
                {item.propertyType && (
                    <Badge variant="light" style={{ fontSize: 11, color: '#555' }}>
                        {item.propertyType}
                    </Badge>
                )}
                {isOZ && (
                    <Badge variant="success" style={{ fontSize: 11 }}>
                        Opportunity Zone
                    </Badge>
                )}
                {isTargeted && (
                    <Badge style={{ fontSize: 11, backgroundColor: '#d64545', color: '#fff' }}>
                        {item.discoveryContext?.matchReason === 'corner_lot_and_park'
                            ? 'Corner Lot + Park'
                            : item.discoveryContext?.matchReason === 'corner_lot'
                              ? 'Corner Lot'
                              : item.discoveryContext?.matchReason === 'near_park'
                                ? 'Near Park'
                                : 'Targeted'}
                    </Badge>
                )}
                {/* User label badge */}
                {item.label && labelColor && (
                    <Badge
                        style={{
                            fontSize: 11,
                            backgroundColor: labelColor,
                            color: '#fff',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setLabelOpen(!labelOpen);
                        }}
                    >
                        {labelDisplay || item.label}
                        <FaChevronDown style={{ marginLeft: 4, fontSize: 9 }} />
                    </Badge>
                )}
                {item.sourceName && !isTargeted && (
                    <span style={{ fontSize: 11, color: '#999' }}>{item.sourceName}</span>
                )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                {onOpenChannel && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenChannel(item);
                        }}
                        className="btn btn-sm btn-outline-primary"
                        style={{ fontSize: 12, padding: '3px 10px' }}
                    >
                        <FaDoorOpen className="mr-1" /> Open Channel
                    </button>
                )}
                {item.sourceUrl && (
                    <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: 12, padding: '3px 10px' }}
                    >
                        <FaExternalLinkAlt className="mr-1" /> Source
                    </a>
                )}
                {/* Label button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setLabelOpen(!labelOpen);
                    }}
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 12, padding: '3px 10px', marginLeft: 'auto' }}
                    disabled={labelLoading}
                >
                    {labelLoading ? '...' : item.label ? 'Edit Label' : 'Add Label'}
                </button>
            </div>
            {/* Label dropdown */}
            {labelOpen && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 8,
                        right: 14,
                        background: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 10,
                        minWidth: 140,
                        padding: 4,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {realEstateListingsApi.RE_LABEL_OPTIONS.map((opt) => (
                        <div
                            key={opt.value}
                            onClick={() => handleLabelSelect(opt.value)}
                            style={{
                                padding: '6px 10px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                background: item.label === opt.value ? '#f0f4ff' : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f7f9fc';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = item.label === opt.value ? '#f0f4ff' : 'transparent';
                            }}
                        >
                            {opt.value && (
                                <span
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: realEstateListingsApi.RE_LABELS[opt.value] || '#ccc',
                                        display: 'inline-block',
                                    }}
                                />
                            )}
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default PropertyCard;
